import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useScrollLock } from '@/hooks/useScrollLock'
import { useNavigate } from 'react-router-dom'
import { useFormatMessage } from '@/translations/useFormatMessage'
import { SvgIcon } from '@mui/material'
import useMediaQuery from '@mui/material/useMediaQuery'
import SwipeableDrawer from '@mui/material/SwipeableDrawer'
import { Backdrop } from '@/components/common/ui/Modal/Modal.styled'
import { SignInForm } from './components/SignInForm/SignInForm'
import { VerifyOtpStep } from './components/VerifyOtpStep/VerifyOtpStep'
import {
	StyledHeaderBox,
	StyledTitle,
	StyledCloseButton,
	StyledModalCard,
	signInDrawerPaperSx,
	DrawerPuller,
} from './SignInModal.styled'
import { PATHS } from '@/router/paths'
import { breakpoints } from '@/styles/designTokens'
import type { SignInModalProps, SignInModalState } from './SignInModal.types'

const TITLE_ID = 'signin-modal-title'

export function SignInModal({
	isOpen,
	onClose,
	locationRef,
	locationId,
	locationCountryCode,
	returnPath,
}: SignInModalProps) {
	const navigate = useNavigate()
	const { t } = useFormatMessage()
	const isMobile = useMediaQuery(`(max-width: ${breakpoints.mobile}px)`)
	const [state, setState] = useState<SignInModalState>({
		isVerifyStepShown: false,
		phoneOrEmailInput: '',
		otpInput: '',
		isRequestInProgress: false,
		errorMessage: null,
		correlationId: null,
		channel: null,
		identifier: null,
	})

	const handleLoginSuccess = () => {
		handleClose()
		void navigate(returnPath ?? PATHS.menu(locationRef))
	}

	const cardRef = useRef<HTMLDivElement>(null)
	const previousFocusRef = useRef<HTMLElement | null>(null)
	const phoneInputRef = useRef<HTMLInputElement | null>(null)

	// Stable identity: an inline arrow here would change every render, which
	// cascades into SignInForm's remount-focus effect re-running on every
	// keystroke elsewhere in the form (e.g. stealing focus from the email field).
	const handlePhoneInputRef = useCallback((ref: HTMLInputElement | null) => {
		phoneInputRef.current = ref
	}, [])

	// Focus return — restore focus to trigger element when modal closes.
	// On mobile SwipeableDrawer handles this internally; keep for desktop.
	useEffect(() => {
		if (!isOpen) return
		previousFocusRef.current = document.activeElement as HTMLElement
		return () => {
			previousFocusRef.current?.focus()
		}
	}, [isOpen])

	// Auto-focus phone input on open (step 1 only).
	// On mobile SwipeableDrawer's focus trap handles initial focus; this effect
	// still fires but targets the phone input specifically for both paths.
	useEffect(() => {
		if (!isOpen || state.isVerifyStepShown) return

		if (phoneInputRef.current) {
			phoneInputRef.current.focus()
		} else {
			const firstFocusable = cardRef.current?.querySelector<HTMLElement>(
				'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
			)
			firstFocusable?.focus()
		}
	}, [isOpen, state.isVerifyStepShown])

	// position:fixed scroll lock — reliable on iOS Safari and Chrome for both mobile
	// (SwipeableDrawer) and desktop (Backdrop). See useScrollLock for full rationale.
	useScrollLock(isOpen)

	// Keyboard handler — desktop only. On mobile SwipeableDrawer's Modal
	// provides Escape handling and focus trap.
	const handleKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Escape') {
			handleClose()
			return
		}

		if (e.key !== 'Tab' || !cardRef.current) return

		const focusable = Array.from(
			cardRef.current.querySelectorAll<HTMLElement>(
				'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
			),
		)
		if (focusable.length === 0) {
			e.preventDefault()
			return
		}

		const first = focusable[0]
		const last = focusable[focusable.length - 1]

		if (e.shiftKey && document.activeElement === first) {
			e.preventDefault()
			last.focus()
		} else if (!e.shiftKey && document.activeElement === last) {
			e.preventDefault()
			first.focus()
		}
	}

	const handleStateUpdate = (updater: (prev: SignInModalState) => SignInModalState) => {
		setState(updater)
	}

	const handleClose = () => {
		setState({
			isVerifyStepShown: false,
			phoneOrEmailInput: '',
			otpInput: '',
			isRequestInProgress: false,
			errorMessage: null,
			correlationId: null,
			channel: null,
			identifier: null,
		})
		onClose()
	}

	// Inner modal content — shared by mobile (SwipeableDrawer) and desktop (StyledModalCard)
	const modalContent = (
		<>
			<StyledHeaderBox>
				<StyledTitle id={TITLE_ID}>
					{state.isVerifyStepShown
						? t(
								state.channel === 'EMAIL'
									? 'signin:modal:verifyEmailTitle'
									: 'signin:modal:verifyTitle',
							)
						: t('signin:modal:signInTitle')}
				</StyledTitle>
				<StyledCloseButton
					onClick={handleClose}
					aria-label={t('signin:modal:closeButton')}
					size="small"
				>
					<SvgIcon>
						<path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
					</SvgIcon>
				</StyledCloseButton>
			</StyledHeaderBox>

			{state.isVerifyStepShown ? (
				<VerifyOtpStep
					state={state}
					setState={handleStateUpdate}
					onClose={handleClose}
					locationRef={locationRef}
					locationId={locationId}
					onLoginSuccess={handleLoginSuccess}
				/>
			) : (
				<SignInForm
					state={state}
					setState={handleStateUpdate}
					onClose={handleClose}
					locationRef={locationRef}
					locationId={locationId}
					locationCountryCode={locationCountryCode}
					onPhoneInputRef={handlePhoneInputRef}
				/>
			)}
		</>
	)

	if (!isOpen) return null

	if (isMobile) {
		return (
			<SwipeableDrawer
				anchor="bottom"
				open={isOpen}
				onOpen={() => {}}
				onClose={handleClose}
				aria-labelledby={TITLE_ID}
				slotProps={{ paper: { sx: signInDrawerPaperSx } }}
				disableSwipeToOpen
			>
				<DrawerPuller aria-hidden="true" />
				{modalContent}
			</SwipeableDrawer>
		)
	}

	return createPortal(
		<Backdrop isEdit={false} role="presentation">
			<StyledModalCard
				ref={cardRef}
				role="dialog"
				aria-modal="true"
				aria-labelledby={TITLE_ID}
				onKeyDown={handleKeyDown}
				onClick={(e) => e.stopPropagation()}
				isEdit={false}
				width={400}
			>
				{modalContent}
			</StyledModalCard>
		</Backdrop>,
		document.body,
	)
}
